// Settings -> Filters: the user deficiency rules (rules.js). Every change applies live:
// the page re-asks the server, which rebuilds the deficiencies, the crash correlation and
// the verdicts under the new rules.
import React from 'react';
import { Modal } from '../Overview/OverviewDialogs';
import { ANALYSIS_NAMES } from '../Overview/overviewModel';
import { DEFAULT_RULES, NET_CONDITIONS, NET_SOURCES, NET_SUPPLY, RULE_ANALYSES, STATION_FT, isDefaultRules } from './rules';

const Num = ({ id, value, onChange, step = 1, min = 0, disabled, unit, width = 80 }) => (
  <span className="so-rule-num">
    <input id={id} type="number" step={step} min={min} value={value} disabled={disabled} style={{ width }}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
    {unit && <span className="safety-muted">{unit}</span>}
  </span>
);

const Switch = ({ id, checked, onChange, label }) => (
  <label className="so-rule-switch" htmlFor={id}>
    <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>{label}</span>
  </label>
);

const SOURCE_NAME = Object.fromEntries(NET_SOURCES.map(([k, label]) => [k, label]));

/** Part B: the other SOP conditions, from the road network — which source, in which order. */
function NetworkRules({ net, set }) {
  const move = (i, d) => {
    const pr = [...net.priority];
    const j = i + d;
    if (j < 0 || j >= pr.length) return;
    [pr[i], pr[j]] = [pr[j], pr[i]];
    set(['priority'], pr);
  };
  const supplyText = (c) => net.priority.filter((k) => NET_SUPPLY[k].supplies.includes(c))
    .map((k) => `${SOURCE_NAME[k]}${NET_SUPPLY[k].complete.includes(c) ? ' (complete)' : ''}`).join(' → ');
  return (
    <section className="so-rule-block">
      <h3>Passing zones near road features · SOP TS-2021-001</h3>
      <p className="safety-muted">A marked passing zone is also a deficiency (severe) within these distances of each feature
        below. The features come from the road network; &quot;Road network sources&quot; under the map shows what was taken
        from where.</p>
      <div className="so-net-priority">
        <span className="so-net-sub">Where to take the data from, first to last</span>
        <ol>
          {net.priority.map((k, i) => (
            <li key={k}>
              <b>{SOURCE_NAME[k]}</b> <span className="safety-muted">· {NET_SOURCES.find(([s]) => s === k)[2]}</span>
              <span className="so-net-move">
                <button type="button" aria-label={`Move ${SOURCE_NAME[k]} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button type="button" aria-label={`Move ${SOURCE_NAME[k]} down`} disabled={i === net.priority.length - 1} onClick={() => move(i, 1)}>↓</button>
              </span>
            </li>
          ))}
        </ol>
        <label className="so-rule-switch">
          <input type="radio" name="so-net-mode" checked={net.mode === 'fill'} onChange={() => set(['mode'], 'fill')} />
          <span>Fill the gaps: a lower source is used only where the ones above it have no data or are partial</span>
        </label>
        <label className="so-rule-switch">
          <input type="radio" name="so-net-mode" checked={net.mode === 'union'} onChange={() => set(['mode'], 'union')} />
          <span>Add everything: every source adds the features the ones above it miss</span>
        </label>
      </div>
      <table className="ov-crit so-net-table">
        <thead><tr><th>Feature</th><th>Approaching</th><th>Departing</th><th>Take it from</th></tr></thead>
        <tbody>
          {NET_CONDITIONS.map(([c, label]) => {
            const rc = net.conditions[c];
            return (
              <tr key={c}>
                <td>
                  <Switch id={`so-net-${c}`} checked={rc.on} onChange={(v) => set(['conditions', c, 'on'], v)} label={label} />
                </td>
                <td><Num id={`so-net-${c}-ap`} value={rc.approachFt} step={10} unit="ft" disabled={!rc.on} width={64}
                  onChange={(v) => set(['conditions', c, 'approachFt'], v)} /></td>
                <td><Num id={`so-net-${c}-dp`} value={rc.departFt} step={10} unit="ft" disabled={!rc.on} width={64}
                  onChange={(v) => set(['conditions', c, 'departFt'], v)} /></td>
                <td>
                  <select className="ov-select" aria-label={`Source of ${label}`} value={rc.source} disabled={!rc.on}
                    onChange={(e) => set(['conditions', c, 'source'], e.target.value)}
                    title={`Available from: ${supplyText(c)}`}>
                    <option value="auto">In the order above</option>
                    {NET_SOURCES.filter(([k]) => NET_SUPPLY[k].supplies.includes(c)).map(([k, name]) => (
                      <option key={k} value={k}>{name} only</option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="safety-muted">
        Complete sources list every such feature where they have data: for that feature the sources below them are not
        consulted there. MassDOT lists only the signals it maintains, MBTA bus stops, hospitals (no fire or EMS stations)
        and no two-way left-turn lanes, so OpenStreetMap completes those. Driveways of a &quot;significant traffic
        generator&quot; have no data source and are not checked. OpenStreetMap data © OpenStreetMap contributors (ODbL).
      </p>
    </section>
  );
}

export default function FiltersDialog({ rules, onChange, onClose }) {
  const set = (path, value) => {
    const next = JSON.parse(JSON.stringify(rules));
    let o = next;
    path.slice(0, -1).forEach((k) => { o = o[k]; });
    o[path[path.length - 1]] = value;
    onChange(next);
  };
  const sop = rules.psdSop;
  return (
    <Modal title="Filters" onClose={onClose} tall>
      <p className="safety-muted safety-modal-lede ov-lede-compact">
        Which stations and stretches count as deficient. Changes apply at once to the map, the tables, the crash
        correlation, the statistical support and the PDF. Stations are {STATION_FT} ft apart. Kept in this browser.
      </p>

      <section className="so-rule-block">
        <h3>Minimum run length</h3>
        <p className="safety-muted">A deficiency counts only when at least this many stations in a row are deficient.</p>
        <table className="ov-crit">
          <tbody>
            {RULE_ANALYSES.map((a) => (
              <tr key={a}>
                <td><b>{a}</b> <span className="safety-muted">{ANALYSIS_NAMES[a]}</span></td>
                <td>
                  <Num id={`so-min-${a}`} value={rules.minStations[a]} min={1} unit="stations"
                    onChange={(v) => set(['minStations', a], v)} />
                  <span className="safety-muted"> = {(Number(rules.minStations[a]) || 0) * STATION_FT} ft</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="so-rule-block">
        <h3>Guardrail warrant (GRW) gaps</h3>
        <Switch id="so-grw-gap" checked={rules.grwGap.on} onChange={(v) => set(['grwGap', 'on'], v)}
          label="Join two warranted stretches across a short gap" />
        <div className="so-rule-row">
          <span>Gap with no data, up to</span>
          <Num id="so-grw-nodata" value={rules.grwGap.noDataFt} step={10} unit="ft" disabled={!rules.grwGap.on}
            onChange={(v) => set(['grwGap', 'noDataFt'], v)} />
        </div>
        <div className="so-rule-row">
          <span>Gap with no warrant, up to</span>
          <Num id="so-grw-nowarrant" value={rules.grwGap.noWarrantFt} step={10} unit="ft" disabled={!rules.grwGap.on}
            onChange={(v) => set(['grwGap', 'noWarrantFt'], v)} />
        </div>
        <p className="safety-muted">A warranted stretch where an existing guardrail runs on that side of travel is shielded,
          not a deficiency. Guardrails come from the road network in the source order below: our own guardrail
          detections once they run, then the MassDOT guardrail inventory.</p>
        <Switch id="so-grw-ignore" checked={rules.grwShield.ignoreInventory}
          onChange={(v) => set(['grwShield', 'ignoreInventory'], v)}
          label="Show our GRW results regardless of existing guardrails" />
      </section>

      <section className="so-rule-block">
        <h3>Passing zones (PSD) · MassDOT SOP TS-2021-001</h3>
        <p className="safety-muted">A marked passing zone is a deficiency where:</p>
        <Switch id="so-sop-ix" checked={sop.intersection.on} onChange={(v) => set(['psdSop', 'intersection', 'on'], v)}
          label="it is near an intersection (severe)" />
        <div className="so-rule-row">
          <span>within</span>
          <Num id="so-sop-approach" value={sop.intersection.approachFt} step={10} unit="ft approaching" disabled={!sop.intersection.on}
            onChange={(v) => set(['psdSop', 'intersection', 'approachFt'], v)} />
          <span>and</span>
          <Num id="so-sop-depart" value={sop.intersection.departFt} step={10} unit="ft departing" disabled={!sop.intersection.on}
            onChange={(v) => set(['psdSop', 'intersection', 'departFt'], v)} />
        </div>
        <div className="so-rule-row so-rule-indent">
          <span>Intersections from</span>
          <Switch id="so-ix-isd" checked={rules.intersectionSources.isd} onChange={(v) => set(['intersectionSources', 'isd'], v)}
            label="the ISD analysis" />
          <Switch id="so-ix-crash" checked={rules.intersectionSources.crashJunctions}
            onChange={(v) => set(['intersectionSources', 'crashJunctions'], v)} label="junctions in the crash records" />
        </div>
        <Switch id="so-sop-zone" checked={sop.minZone.on} onChange={(v) => set(['psdSop', 'minZone', 'on'], v)}
          label="the zone is shorter than (moderate)" />
        <div className="so-rule-row so-rule-indent">
          <Num id="so-sop-zone-ft" value={sop.minZone.ft} step={50} unit="ft" disabled={!sop.minZone.on}
            onChange={(v) => set(['psdSop', 'minZone', 'ft'], v)} />
        </div>
        <Switch id="so-sop-speed" checked={sop.minSpeed.on} onChange={(v) => set(['psdSop', 'minSpeed', 'on'], v)}
          label="the speed limit is below (moderate)" />
        <div className="so-rule-row so-rule-indent">
          <Num id="so-sop-speed-mph" value={sop.minSpeed.mph} step={5} unit="mph" disabled={!sop.minSpeed.on}
            onChange={(v) => set(['psdSop', 'minSpeed', 'mph'], v)} />
        </div>
        <p className="safety-muted">An Unsafe pass (the marking allows passing where the passing sight distance is not
          available) is always a deficiency, severe.</p>
      </section>

      <NetworkRules net={rules.network} set={(path, v) => set(['network', ...path], v)} />

      <div className="safety-modal-foot">
        <span className="safety-muted">Defaults: the existing definitions, the new values only where none existed.</span>
        <div className="row-actions">
          <button type="button" className="safety-btn safety-btn-ghost" disabled={isDefaultRules(rules)}
            onClick={() => onChange(JSON.parse(JSON.stringify(DEFAULT_RULES)))}>Reset to defaults</button>
          <button type="button" className="safety-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </Modal>
  );
}
